import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '../types/user';
import { logger } from '../utils/logger';

// Re-export User type for backward compatibility
export type { User } from '../types/user';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  login: (user: User, token: string, refreshToken?: string | null) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // State
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      // Actions
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setToken: (token) => set({ token }),
      
      setRefreshToken: (refreshToken) => set({ refreshToken }),
      
      login: (user, token, refreshToken) => {
        logger.debug('🔑 AuthStore - User Login:', {
          user: user ? { 
            id: user.id, 
            email: user.email, 
            firstName: user.firstName, 
            lastName: user.lastName, 
            role: user.role 
          } : null,
          hasToken: !!token,
          hasRefreshToken: !!refreshToken
        });
        
        return set({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          isLoading: false
        });
      },
      
      logout: () => set({
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false
      }),
      
      setLoading: (isLoading) => set({ isLoading })
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated
      }),
      onRehydrateStorage: () => (state) => {
        logger.debug('🔄 AuthStore - Rehydrated from localStorage:', {
          user: state?.user ? {
            id: state.user.id,
            email: state.user.email,
            firstName: state.user.firstName,
            lastName: state.user.lastName,
            role: state.user.role
          } : null,
          isAuthenticated: state?.isAuthenticated,
          hasToken: !!state?.token
        });
      }
    }
  )
);

export { useAuthStore };